import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, ChevronLeft, CheckCircle, User, Heart, Building2, Shield, FileText, Upload, Camera, X, RotateCcw, Calendar as CalendarIcon, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';

const SignatureCanvas = React.lazy(() => import('react-signature-canvas'));

const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;
const DATE_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine',
  'Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma',
  'Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

const ALLERGY_OPTIONS = ['Ninguno', 'Penicilina', 'Yodo', 'Antiinflamatorios', 'Venofer', 'Aspirina'];

// Lazily load the Google Maps JS API (Places) once, using a runtime clinic key
let _gmapsPromise = null;
const loadGoogleMaps = (apiKey) => {
  if (typeof window === 'undefined' || !apiKey) return Promise.resolve(false);
  if (window.google?.maps?.places) return Promise.resolve(true);
  if (_gmapsPromise) return _gmapsPromise;
  _gmapsPromise = new Promise((resolve) => {
    // Surface invalid-key / billing errors to the console for easier debugging
    window.gm_authFailure = () => {
      console.error('[Google Maps] Authentication failed — check the API key, that Maps JavaScript API + Places API are enabled, billing is on, and the HTTP referrer restriction allows this domain.');
      _gmapsPromise = null;
      resolve(false);
    };
    // Ensure the autocomplete dropdown renders above any overlays
    if (!document.getElementById('pac-zindex-style')) {
      const st = document.createElement('style');
      st.id = 'pac-zindex-style';
      st.textContent = '.pac-container{z-index:100000 !important}';
      document.head.appendChild(st);
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // Poll until the Places library is actually ready
      let tries = 0;
      const iv = setInterval(() => {
        tries += 1;
        if (window.google?.maps?.places?.Autocomplete) {
          clearInterval(iv);
          resolve(true);
        } else if (tries > 100) { // ~10s
          clearInterval(iv);
          console.error('[Google Maps] Places library did not load in time.');
          resolve(false);
        }
      }, 100);
    };
    s.onerror = () => { _gmapsPromise = null; resolve(false); };
    document.head.appendChild(s);
  });
  return _gmapsPromise;
};

// Address autocomplete on a fully controlled input (clearing works natively -> validation is reliable).
// Uses Google's new Autocomplete Data API (AutocompleteSuggestion) with a custom dropdown,
// falling back to the legacy Autocomplete widget for older keys.
const AddressAutocompleteInput = ({ apiKey, value, onChange, onSelect, placeholder, className, testId, queryPrefix }) => {
  const inputRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const legacyInitedRef = React.useRef(false);
  const sessionTokenRef = React.useRef(null);
  const placesLibRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const blurTimerRef = React.useRef(null);
  const queryPrefixRef = React.useRef(queryPrefix || '');
  React.useEffect(() => { queryPrefixRef.current = queryPrefix || ''; }, [queryPrefix]);
  const [ready, setReady] = React.useState(false);
  const [useLegacy, setUseLegacy] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!apiKey) return;
    loadGoogleMaps(apiKey).then(async (ok) => {
      if (!ok || cancelled) return;
      try {
        const places = await window.google.maps.importLibrary('places');
        if (cancelled) return;
        placesLibRef.current = places;
        if (places.AutocompleteSuggestion && places.AutocompleteSessionToken) {
          setReady(true);
        } else if (places.Autocomplete && inputRef.current && !legacyInitedRef.current) {
          // Legacy fallback for older keys
          setUseLegacy(true);
          const ac = new places.Autocomplete(inputRef.current, {
            types: ['address'], componentRestrictions: { country: ['us'] }, fields: ['address_components'],
          });
          ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            const comps = place?.address_components || [];
            const g = (t) => { const c = comps.find((x) => x.types.includes(t)); return c ? c.long_name : ''; };
            onSelect({
              street: [g('street_number'), g('route')].filter(Boolean).join(' '),
              city: g('locality') || g('sublocality') || g('postal_town') || g('administrative_area_level_2'),
              state: g('administrative_area_level_1'), zip: g('postal_code'),
            });
          });
          legacyInitedRef.current = true;
        }
      } catch (e) { console.error('[Places] init error', e); }
    });
    return () => { cancelled = true; };
  }, [apiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSuggestions = React.useCallback((text) => {
    const places = placesLibRef.current;
    if (!places?.AutocompleteSuggestion || !text || text.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    if (!sessionTokenRef.current) sessionTokenRef.current = new places.AutocompleteSessionToken();
    // When a pharmacy name is chosen, scope the search to that chain so results are its locations
    const prefix = (queryPrefixRef.current || '').trim();
    const queryInput = prefix ? `${prefix} ${text}` : text;
    places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: queryInput, sessionToken: sessionTokenRef.current, includedRegionCodes: ['us'],
    }).then(({ suggestions }) => {
      const list = (suggestions || []).map((s) => s.placePrediction).filter(Boolean);
      setSuggestions(list);
      setOpen(list.length > 0);
    }).catch((e) => { console.error('[Places] fetch error', e); setSuggestions([]); setOpen(false); });
  }, []);

  const handleInput = (v) => {
    onChange(v);
    if (!v) { onSelect({ street: '', city: '', state: '', zip: '' }); setSuggestions([]); setOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  };

  const handlePick = async (prediction) => {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'nationalPhoneNumber'] });
      const comps = place.addressComponents || [];
      const g = (t) => { const c = comps.find((x) => (x.types || []).includes(t)); return c ? (c.longText || c.shortText || '') : ''; };
      const street = [g('street_number'), g('route')].filter(Boolean).join(' ');
      onSelect({
        street,
        city: g('locality') || g('sublocality') || g('postal_town') || g('administrative_area_level_2'),
        state: g('administrative_area_level_1'), zip: g('postal_code'),
        phone: place.nationalPhoneNumber || '',
      });
      sessionTokenRef.current = null; // end session after a selection
    } catch (e) { console.error('[Places] pick error', e); }
    setOpen(false); setSuggestions([]);
  };

  const showDropdown = ready && !useLegacy && open && suggestions.length > 0;

  return (
    <div ref={wrapRef} className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={() => { blurTimerRef.current = setTimeout(() => setOpen(false), 200); }}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {showDropdown && (
        <div className="absolute z-[100000] mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="address-suggestions">
          {suggestions.map((p, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); if (blurTimerRef.current) clearTimeout(blurTimerRef.current); handlePick(p); }}
              className="flex w-full items-start gap-2 px-4 py-3 text-left text-base hover:bg-blue-50 border-b border-slate-100 last:border-0"
            >
              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
              <span className="text-slate-700">{p.text?.text || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// 5 most used pharmacy chains in the US (for autocomplete)
const COMMON_PHARMACIES = ['CVS Pharmacy', 'Walgreens', 'Walmart Pharmacy', 'Rite Aid', 'Costco Pharmacy'];

const PharmacyInput = ({ value, onChange, placeholder, invalid }) => {
  const [open, setOpen] = useState(false);
  const q = (value || '').toLowerCase();
  const suggestions = q
    ? COMMON_PHARMACIES.filter(p => p.toLowerCase().includes(q) && p.toLowerCase() !== q)
    : COMMON_PHARMACIES;
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={`h-14 text-lg mt-1 ${invalid ? 'border-red-500 ring-2 ring-red-200' : ''}`}
        data-testid="pharmacy-name-input"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="pharmacy-suggestions">
          {suggestions.map(p => (
            <button
              key={p}
              type="button"
              onMouseDown={() => { onChange(p); setOpen(false); }}
              className="w-full text-left px-4 py-3 text-base text-slate-700 hover:bg-blue-50 transition-colors"
            >{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};


// Phone input: digits only, formatted as (XXX) XXX-XXXX
const formatPhone = (val) => {
  const d = (val || '').replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const PhoneInput = ({ value, onChange, placeholder, testid, invalid }) => (
  <Input
    type="tel"
    inputMode="numeric"
    value={value}
    onChange={e => onChange(formatPhone(e.target.value))}
    placeholder={placeholder || '(000) 000-0000'}
    className={`h-14 text-lg mt-1 ${invalid ? 'border-red-500 ring-2 ring-red-200' : ''}`}
    data-testid={testid}
  />
);

// Date input: digits only, masked as MM-DD-YYYY
const formatDate = (val) => {
  const d = (val || '').replace(/\D/g, '').slice(0, 8);
  if (d.length < 3) return d;
  if (d.length < 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
};

const DateInput = ({ value, onChange, testid, invalid }) => {
  const [open, setOpen] = useState(false);
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value || '');
  let parsed;
  if (m) {
    const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
    if (!isNaN(d.getTime())) parsed = d;
  }
  const currentYear = new Date().getFullYear();
  return (
    <div className="relative mt-1">
      <Input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={e => onChange(formatDate(e.target.value))}
        placeholder="MM-DD-YYYY"
        className={`h-14 text-lg pr-14 ${invalid ? 'border-red-500 ring-2 ring-red-200' : ''}`}
        data-testid={testid}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-500 hover:text-violet-600 hover:bg-violet-50 transition-colors"
            data-testid={testid ? `${testid}-calendar-btn` : undefined}
            aria-label="Open calendar"
          >
            <CalendarIcon className="w-5 h-5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={parsed}
            defaultMonth={parsed || new Date(1990, 0)}
            captionLayout="dropdown-buttons"
            fromYear={1900}
            toYear={currentYear}
            classNames={{
              vhidden: 'sr-only',
              caption_label: 'hidden',
              caption_dropdowns: 'flex justify-center gap-2',
              dropdown: 'border border-slate-300 rounded-md px-2 py-1 text-sm bg-white',
            }}
            onSelect={(d) => {
              if (d) {
                onChange(`${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`);
                setOpen(false);
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Multi-select dropdown with checkboxes
const MultiSelect = ({ options, values, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const toggle = (o) => {
    onChange(values.includes(o) ? values.filter(v => v !== o) : [...values, o]);
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full h-14 px-4 text-lg mt-1 rounded-md border border-slate-300 bg-white text-left flex items-center justify-between hover:border-slate-400 transition-colors"
        data-testid="referral-multiselect"
      >
        <span className={values.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
          {values.length ? values.join(', ') : placeholder}
        </span>
        <ChevronRight className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`} />
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 bottom-full mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          {options.map(o => (
            <button
              key={o}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); toggle(o); }}
              className="w-full text-left px-4 py-3 text-base flex items-center gap-3 hover:bg-violet-50 transition-colors"
            >
              <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${values.includes(o) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
                {values.includes(o) && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </span>
              <span className="text-slate-700">{o}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const StepIndicator = ({ steps, current }) => (
  <div className="flex items-center justify-center gap-1 mb-6">
    {steps.map((_, i) => (
      <React.Fragment key={i}>
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all ${
          i < current ? 'bg-teal-600 text-white' : i === current ? 'bg-blue-600 text-white ring-4 ring-blue-100' : 'bg-slate-200 text-slate-500'
        }`}>
          {i < current ? <CheckCircle className="w-4 h-4" /> : i + 1}
        </div>
        {i < steps.length - 1 && <div className={`w-5 h-0.5 ${i < current ? 'bg-teal-500' : 'bg-slate-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const ToggleGroup = ({ options, value, onChange, invalid }) => {
  const n = options.length;
  const activeIndex = options.indexOf(value);
  return (
    <div
      className={`relative inline-grid p-1 rounded-full border ${invalid ? 'bg-red-50 border-red-400 ring-2 ring-red-200' : 'bg-violet-100/70 border-violet-200'}`}
      style={{ gridTemplateColumns: `repeat(${n}, minmax(72px, 1fr))` }}
    >
      {activeIndex >= 0 && (
        <span
          className="absolute top-1 bottom-1 rounded-full bg-violet-500 shadow-md transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          style={{ width: `calc((100% - 0.5rem) / ${n})`, left: '0.25rem', transform: `translateX(${activeIndex * 100}%)` }}
        />
      )}
      {options.map(o => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`relative z-10 px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 active:scale-95 ${
            value === o ? 'text-white' : 'text-violet-700/70 hover:text-violet-800'
          }`}
        >{o}</button>
      ))}
    </div>
  );
};

const PhotoUpload = ({ label, file, onUpload, onRemove, promptText, testid }) => (
  <div className="space-y-2">
    <Label className="text-base font-semibold text-slate-700">{label}</Label>
    {file ? (
      <div className="relative inline-block">
        <img src={URL.createObjectURL(file)} alt={label} className="h-32 rounded-xl border-2 border-teal-300 object-cover" />
        <button onClick={onRemove} className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all">
        <Camera className="w-8 h-8 text-slate-400 mb-1" />
        <span className="text-sm text-slate-500">{promptText || 'Take photo or select'}</span>
        <input type="file" accept="image/*" capture="environment" onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); }} className="hidden" data-testid={testid} />
      </label>
    )}
  </div>
);

const T = {
  es: {
    formTitle: 'Información del Paciente', subtitle: 'Tu Clinica Familiar',
    langPrompt: 'Seleccione su idioma preferido', langLabel: 'Idioma',
    continue: 'Continuar', next: 'Siguiente', back: 'Atrás', submit: 'Enviar Formulario', submitting: 'Enviando...',
    steps: ['Información Personal','Información Médica','Farmacia','Seguro y Documentos','Responsabilidad Financiera','HIPAA','Consentimiento de Telemedicina','Firma y Envío'],
    // Step 0
    firstName: 'Nombre', lastName: 'Apellido', address: 'Dirección', street: 'Calle y número',
    city: 'Ciudad', state: 'Estado', zip: 'Código Postal', phone: 'Teléfono', email: 'Email',
    contactMethod: 'Comunicación preferida', call: 'Llamada',
    dob: 'Fecha de Nacimiento', sex: 'Sexo', male: 'Hombre', female: 'Mujer',
    isMinor: 'Menor de 18 años', yes: 'Si', no: 'No',
    // Step 1
    allergies: 'Alergias a medicamentos', otherAllergy: 'Alérgico a otro medicamento?',
    specify: 'Especifique...', pregnant: 'Está embarazada?',
    emergencyContact: 'Contacto de emergencia?', relationship: 'Relación',
    takingMeds: 'Está tomando algún medicamento?', medsDetail: 'Mencione qué medicamentos...',
    referral: 'Cómo se enteró de nosotros?', select: 'Seleccione', selectMultiple: 'Seleccione una o más opciones',
    referralOptions: ['Referencia','Google','Facebook','Instagram','Radio','Folleto','Letrero / Ubicación física'],
    // Step 2 - Pharmacy
    pharmacyIntro: 'Información de su Farmacia para enviar medicamentos en caso de ser necesario. En relación con su consulta de telemedicina de hoy con A Plus Telehealth, LLC, {clinic} puede, a solicitud suya, enviar sus recetas electrónicamente a su farmacia. Si desea que sus recetas se envíen a su farmacia, proporcione el nombre, la dirección y el número de teléfono de su farmacia.',
    // Step 2
    pharmacyInfo: 'Tu Clinica Hispana puede enviar sus recetas electrónicamente a su farmacia.',
    pharmacyName: 'Nombre de su Farmacia', pharmacyAddress: 'Dirección de Farmacia',
    pfChecking: 'Verificando dirección de la farmacia...',
    pfMatched: 'Dirección de farmacia verificada',
    pfNoMatch: 'No pudimos verificar esta dirección de farmacia. Por favor confirme la dirección.',
    // Step 3
    hasInsurance: 'Tiene Seguro?', insuranceFront: 'Foto del Seguro (Frente)',
    insuranceBack: 'Foto del Seguro (Atrás)', idPhoto: 'Foto del ID',
    insuranceNote: 'Favor de entregarle al Front Desk su tarjeta de seguro y ID.',
    photoPrompt: 'Tomar foto o seleccionar',
    // Step 4 - Financial Responsibility
    financialTitle: 'Responsabilidad Financiera',
    financialText: 'A Plus Telehealth, LLC es una clínica privada que ofrece una atención de cuidado para sus pacientes sin ningún tipo de seguro médico; de esta manera podemos reducir los gastos generales y cobrar a los pacientes menos. Esto nos permite pasar más tiempo con nuestros pacientes y que nadie se sienta apresurado. Entiendo que yo soy personal y financieramente responsable por todos los gastos relacionados con el servicio realizado por A Plus Telehealth, LLC o {clinic}, durante esta u otras visitas.',
    financialAgree: 'He leído y entendido esta política y deseo recibir los servicios de A Plus Telehealth, LLC o {clinic}.',
    // Step 5 - HIPAA
    hipaaTitle: 'HIPAA - Información Educativa al Paciente y Preguntas Más Frecuentes',
    hipaaFull: '¿Qué es HIPAA? El Seguro de Salud de Portabilidad y Responsabilidad de la Ley de 1996 (HIPAA) es una ley aprobada por el Congreso para proteger la privacidad de los pacientes en lo que respecta a los expedientes médicos y controlar el flujo de información de salud. Por otra parte, HIPAA fue diseñada con el propósito de bajar los costos administrativos médicos estableciendo normas para la presentación y tramitación de los reclamos de seguros. Los reglamentos de HIPAA afectan a las personas en todos los niveles de atención médica, incluyendo a los pacientes y sus médicos.\n\n¿Qué es TPO? Tratamiento, pago y operaciones (TPO) incluye los procesos habituales que intervienen en recibir atención médica. Hay varios ejemplos que abarcan TPO. A menudo es necesario compartir su información de salud entre los proveedores de asistencia médica, tales como proporcionar una remisión a un especialista. Esta es una parte del tratamiento. Información acerca de su diagnóstico u otro tipo de información de su salud que requieran pago para las compañías de seguros. La evaluación de los expedientes médicos garantiza una alta calidad de atención prestada por nuestros médicos que consideramos parte de las operaciones.\n\n¿Por qué me debe importar TPO? La legislación de HIPAA esboza diferencias significativas para el manejo de la información médica como TPO, para motivos distintos de TPO. Las leyes creadas por HIPAA están diseñadas para agilizar la asistencia médica para no tener restricciones en el intercambio de información sobre su salud (TPO) y para restringir la información no necesaria para TPO (por ejemplo, información dada a otras personas, incluso miembros de su familia, o razones comerciales).\n\n¿Cómo me afecta HIPAA como paciente? HIPAA beneficia de muchas maneras a los pacientes. Por ejemplo, A Plus Telehealth, LLC proveerá información a los pacientes sobre sus derechos privados. Por otra parte, los nuevos reglamentos hacen ilegal el acto de que los proveedores de atención médica vendan su información de salud para los comercializadores y los anunciantes sin su autorización escrita. Como paciente, usted tiene el derecho de revisar su expediente médico si cree que algo es incorrecto y solicitar un cambio. Sin embargo, solo su médico puede determinar si su expediente médico es incorrecto.\n\n¿HIPAA tiene efectos negativos? La intención de la legislación de HIPAA es mejorar el nivel de privacidad para los pacientes. Sin embargo, la ley exige que el paciente autorice por escrito la información de su salud antes de que sea puesta en libertad por razones distintas de TPO. Por ejemplo, los familiares no pueden llamar a la clínica y obtener cualquier información de salud sin que usted firme una autorización primero. Por favor, comprenda que la clínica está trabajando para proteger la privacidad de todos los pacientes y puede tener pólizas aún más estrictas para tener el control de dicha información.\n\n¿Cuándo se llevarán a cabo estos cambios? A Plus Telehealth, LLC debe cumplir con los reglamentos de HIPAA. Sin embargo, cada clínica está trabajando diligentemente para ser compatible con anticipación de plazo.\n\n¿A quién debo llamar si tengo una pregunta sobre la privacidad de mi expediente médico de esta locación? A Plus Telehealth, LLC — {clinic}.',
    hipaaSignLabel: 'Firma del paciente o Representante (Nombre Completo)',
    fullName: 'Nombre Completo',
    dateLabel: 'Fecha', dobLabel: 'Fecha de Nacimiento',
    // Step 6 - Telemedicine Informed Consent
    teleTitle: 'Consentimiento Informado de Telemedicina',
    teleFull: 'Descripción de Telemedicina: entiendo que la telemedicina requiere de mí y de un profesional de la salud para comunicar información de forma interactiva a través de una videoconferencia acerca de mi salud, incluyendo antecedentes de salud, síntomas actuales, datos de laboratorio y de diagnóstico.\n\n1. Se podrían discutir los detalles de su historial médico, exámenes, rayos X y pruebas con otros profesionales de la salud por medio de tecnología de video interactivo, audio y telecomunicaciones.\n2. Se le podría realizar un examen visual y físico.\n3. Se le podría pedir a personal técnico, no médico, que entre en el área donde se está realizando la telemedicina.\n4. Se podría grabar video, audio y/o tomar fotografías de las consultas.\n5. Sobre los beneficios y riesgos de la telemedicina (174.5.b del Reglamento): la telemedicina permite proporcionar servicios de calidad sin necesidad de viajar largas distancias, pero a la vez presenta algunos riesgos:\n   a. Existe el riesgo, a pesar de nuestros mejores esfuerzos para proteger la privacidad de la información del paciente, de que el protocolo de seguridad pueda fallar causando una violación de la privacidad de la información médica personal.\n   b. Existe el riesgo de un retraso en el diagnóstico y tratamiento médico debido a fallas inesperadas de los equipos electrónicos durante este servicio.\n   c. Entiendo que si la consulta de telemedicina no se puede realizar, seré referido a otro profesional de la salud para una consulta o se programará una consulta en persona (174.5.c del Reglamento).\n6. Entiendo que puedo retirar mi consentimiento en cualquier momento para participar en este medio terapéutico y que entonces se me referiría a otro profesional de la salud.\n7. Cualquier difusión de imágenes o información y ciertas actividades administrativas y operativas necesarias de apoyo a su cuidado no tendrá lugar sin su autorización.\n\nAl firmar este documento, entiendo la información escrita proporcionada anteriormente y por este medio, voluntaria y libremente, doy mi consentimiento para participar en el servicio de salud de telemedicina y la evaluación, valoración y el diagnóstico conveniente para mi condición médica actual.',
    teleFooter: 'Al firmar usted está de acuerdo que ha leído y entendido todo nuestro consentimiento informado de Telemedicina, además de nuestro entendimiento de recibo sobre la comunicación y también para los pacientes que no tienen ningún tipo de seguro, HIPAA y prácticas privadas.',
    // Step 7 - Signature & Agreement
    consentTitle: 'Al firmar usted está de acuerdo:',
    agreementTitle: 'Acuerdo',
    signatureLabel: 'Firma del Paciente', signHere: 'Firme aquí con su dedo o stylus', clear: 'Borrar',
    representative: 'Representante del paciente (si aplica)', repRelationship: 'Relación con el paciente',
    smsConsent: 'Acepto recibir mensajes promocionales enviados a través de un marcador automático, y este acuerdo no es una condición de ninguna compra. También acepto los Términos de Servicio y la Política de Privacidad. 4 mensajes/mes. Pueden aplicarse tarifas de mensajes y datos. Envíe STOP para cancelar en cualquier momento. Envíe HELP para más información.',
    successTitle: 'Formulario Enviado', successMsg: 'Gracias por completar su información. El equipo de la clínica revisará su formulario.',
    newForm: 'Llenar otro formulario',
    // Validations
    vName: 'Nombre y Apellido son requeridos', vPhone: 'Teléfono es requerido',
    vPhoneInvalid: 'El teléfono debe ser un número válido de 10 dígitos',
    vDob: 'Fecha de nacimiento es requerida', vSex: 'Sexo es requerido',
    vDateInvalid: 'La fecha debe tener el formato MM-DD-YYYY',
    vAddress: 'Dirección completa es requerida (calle, ciudad, estado, código postal)',
    vContact: 'Método de comunicación es requerido', vPharmacy: 'Nombre de farmacia es requerido',
    vToggles: 'Por favor responda todas las preguntas de Sí/No',
    vFinancial: 'El nombre es requerido', vHipaa: 'Nombre para HIPAA es requerido', vTele: 'Nombre para Telemedicina es requerido',
    vSig: 'Firma es requerida', vSigName: 'Nombre completo es requerido',
    vError: 'Error al enviar. Intente de nuevo.',
    looksGood: '¡Todo correcto!',
  },
  en: {
    formTitle: 'Patient Information', subtitle: 'Tu Clinica Familiar',
    langPrompt: 'Select your preferred language', langLabel: 'Language',
    continue: 'Continue', next: 'Next', back: 'Back', submit: 'Submit Form', submitting: 'Submitting...',
    steps: ['Personal Information','Medical Information','Pharmacy','Insurance & Documents','Financial Responsibility','HIPAA','Telemedicine Consent','Signature & Submit'],
    firstName: 'First Name', lastName: 'Last Name', address: 'Address', street: 'Street address',
    city: 'City', state: 'State', zip: 'Zip Code', phone: 'Phone', email: 'Email',
    contactMethod: 'Preferred contact method', call: 'Call',
    dob: 'Date of Birth', sex: 'Sex', male: 'Male', female: 'Female',
    isMinor: 'Under 18 years old', yes: 'Yes', no: 'No',
    allergies: 'Drug allergies', otherAllergy: 'Allergic to other medication?',
    specify: 'Specify...', pregnant: 'Are you pregnant?',
    emergencyContact: 'Emergency contact?', relationship: 'Relationship',
    takingMeds: 'Currently taking any medication?', medsDetail: 'List your medications...',
    referral: 'How did you hear about us?', select: 'Select', selectMultiple: 'Select one or more options',
    referralOptions: ['Referral','Google','Facebook','Instagram','Radio','Flyer','Sign / Physical location'],
    pharmacyIntro: 'Your pharmacy information to send medications if needed. In connection with your telemedicine encounter today with A Plus Telehealth, LLC, {clinic} may, at your request, send your prescriptions electronically to your pharmacy. If you would like your prescriptions sent to your pharmacy, we provide the name, address, and phone number of your pharmacy.',
    pharmacyName: 'Pharmacy Name', pharmacyAddress: 'Pharmacy Address',
    pfChecking: 'Verifying pharmacy address...',
    pfMatched: 'Pharmacy address verified',
    pfNoMatch: 'We could not verify this pharmacy address. Please double-check the address.',
    hasInsurance: 'Do you have insurance?', insuranceFront: 'Insurance Card (Front)',
    insuranceBack: 'Insurance Card (Back)', idPhoto: 'ID Photo',
    insuranceNote: 'Please hand your insurance card and ID to the Front Desk.',
    photoPrompt: 'Take photo or select',
    financialTitle: 'Financial Responsibility',
    financialText: 'A Plus Telehealth, LLC is a private clinic that offers care for patients without any type of medical insurance; this way we can reduce overhead costs and charge patients less. This allows us to spend more time with our patients and no one feels rushed. I understand that I am personally and financially responsible for all costs related to the service performed by A Plus Telehealth, LLC or {clinic}, during this or other visits.',
    financialAgree: 'I have read and understood this policy and wish to receive the services of A Plus Telehealth, LLC or {clinic}.',
    hipaaTitle: 'HIPAA - Patient Educational Information & Frequently Asked Questions',
    hipaaFull: 'What is HIPAA? The Health Insurance Portability and Accountability Act of 1996 (HIPAA) is a law passed by Congress to protect the privacy of patients regarding medical records and to control the flow of health information. HIPAA was also designed to lower medical administrative costs by establishing standards for the submission and processing of insurance claims. HIPAA regulations affect people at all levels of health care, including patients and their physicians.\n\nWhat is TPO? Treatment, Payment and Operations (TPO) includes the usual processes involved in receiving health care. There are several examples that cover TPO. It is often necessary to share your health information among health care providers, such as providing a referral to a specialist. This is a part of treatment. Information about your diagnosis or other health information may be required for payment by insurance companies. The evaluation of medical records ensures a high quality of care provided by our physicians, which we consider part of operations.\n\nWhy should I care about TPO? HIPAA legislation outlines significant differences in the handling of medical information as TPO versus for purposes other than TPO. The laws created by HIPAA are designed to streamline health care so there are no restrictions on sharing your health information (TPO) and to restrict information not necessary for TPO (for example, information given to other people, including family members, or for commercial reasons).\n\nHow does HIPAA affect me as a patient? HIPAA benefits patients in many ways. For example, A Plus Telehealth, LLC will provide patients with information about their privacy rights. In addition, the new regulations make it illegal for health care providers to sell your health information to marketers and advertisers without your written authorization. As a patient, you have the right to review your medical record if you believe something is incorrect and to request a change. However, only your physician can determine whether your medical record is incorrect.\n\nDoes HIPAA have negative effects? The intent of HIPAA legislation is to improve the level of privacy for patients. However, the law requires the patient to authorize in writing the release of their health information before it is released for reasons other than TPO. For example, family members cannot call the clinic and obtain any health information without you signing an authorization first. Please understand that the clinic is working to protect the privacy of all patients and may have even stricter policies to control such information.\n\nWhen will these changes take place? A Plus Telehealth, LLC must comply with HIPAA regulations. However, each clinic is working diligently to be compliant ahead of the deadline.\n\nWhom should I call if I have a question about the privacy of my medical record at this location? A Plus Telehealth, LLC — {clinic}.',
    hipaaSignLabel: 'Patient or Representative Signature (Full Name)',
    fullName: 'Full Name',
    dateLabel: 'Date', dobLabel: 'Date of Birth',
    teleTitle: 'Telemedicine Informed Consent',
    teleFull: 'Description of Telemedicine: I understand that telemedicine requires me and a healthcare professional to interactively communicate health information through a video conference, including health history, current symptoms, laboratory and diagnostic data.\n\n1. Details of your medical history, examinations, x-rays and tests may be discussed with other healthcare professionals through interactive video, audio and telecommunications technology.\n2. A visual and physical examination may be performed.\n3. Non-medical technical personnel may be asked to enter the area where the telemedicine is being performed.\n4. Video, audio and/or photographs of the consultations may be recorded.\n5. Regarding the benefits and risks of telemedicine (174.5.b of the Regulation): telemedicine makes it possible to provide quality services without the need to travel long distances, but at the same time it presents some risks:\n   a. There is a risk that, despite our best efforts to protect the privacy of patient information, the security protocol may fail causing a breach of the privacy of personal medical information.\n   b. There is a risk of a delay in medical diagnosis and treatment due to unexpected failures of electronic equipment during this service.\n   c. I understand that if the telemedicine consultation cannot be performed, I will be referred to another healthcare professional for a consultation or an in-person consultation will be scheduled (174.5.c of the Regulation).\n6. I understand that I may withdraw my consent at any time to participate in this therapeutic medium and that I would then be referred to another healthcare professional.\n7. Any dissemination of images or information and certain necessary administrative and operational activities to support your care will not take place without your authorization.\n\nBy signing this document, I understand the written information provided above and hereby voluntarily and freely give my consent to participate in the telemedicine health service and the evaluation, assessment and diagnosis appropriate for my current medical condition.',
    teleFooter: 'By signing you agree that you have read and understood all of our Telemedicine informed consent, as well as our acknowledgment of receipt regarding communication and also for patients who do not have any type of insurance, HIPAA and private practices.',
    consentTitle: 'By signing you agree to:',
    agreementTitle: 'Agreement',
    signatureLabel: 'Patient Signature', signHere: 'Sign here with your finger or stylus', clear: 'Clear',
    representative: 'Patient representative (if applicable)', repRelationship: 'Relationship to patient',
    smsConsent: 'I agree to receive promotional messages sent via an autodialer, and this agreement isn\'t a condition of any purchase. I also agree to the Terms of Service and Privacy Policy. 4 Msgs/Month. Msg & Data Rates may apply. Text STOP to opt out anytime. Text HELP for more information.',
    successTitle: 'Form Submitted', successMsg: 'Thank you for completing your information. The clinic team will review your form.',
    newForm: 'Fill another form',
    vName: 'First and Last name are required', vPhone: 'Phone is required',
    vPhoneInvalid: 'Phone must be a valid 10-digit number',
    vDob: 'Date of birth is required', vSex: 'Sex is required',
    vDateInvalid: 'Date must be in MM-DD-YYYY format',
    vAddress: 'Complete address is required (street, city, state, zip)',
    vContact: 'Contact method is required', vPharmacy: 'Pharmacy name is required',
    vToggles: 'Please answer all Yes/No questions',
    vFinancial: 'Name is required', vHipaa: 'Name for HIPAA is required', vTele: 'Name for Telemedicine is required',
    vSig: 'Signature is required', vSigName: 'Full name is required',
    vError: 'Error submitting. Please try again.',
    looksGood: 'Looks good!',
  }
};

// Clinic logo with automatic retry (cache-busting) on transient load failures.
// If the real logo ultimately fails, it shows the clinic NAME as text — never a
// generic default logo. All failures are logged to the browser console.
const ClinicLogo = ({ logoUrl, clinicName, className, textClassName }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const MAX_RETRIES = 2;

  if (!logoUrl || failed) {
    return (
      <span className={textClassName} data-testid="clinic-logo-name">
        {clinicName || 'Clinic'}
      </span>
    );
  }

  const src = attempt === 0
    ? logoUrl
    : `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;

  return (
    <img
      key={attempt}
      src={src}
      alt={clinicName || 'Clinic logo'}
      className={className}
      data-testid="clinic-logo"
      onError={() => {
        console.error(`[PatientIntake] Clinic logo failed to load (attempt ${attempt + 1}/${MAX_RETRIES + 1}): ${logoUrl}`);
        if (attempt < MAX_RETRIES) {
          const next = attempt + 1;
          setTimeout(() => setAttempt(next), 600 * next); // backoff: 0.6s, 1.2s
        } else {
          console.error(`[PatientIntake] Clinic logo gave up after ${MAX_RETRIES + 1} attempts; showing clinic name instead: ${logoUrl}`);
          setFailed(true);
        }
      }}
    />
  );
};

const PatientIntake = () => {
  const clinicCode = (new URLSearchParams(window.location.search).get('clinic') || '').trim();
  const [clinicInfo, setClinicInfo] = useState(undefined); // undefined=loading, null=invalid, object=valid
  // Serve the logo through our own backend (proxy) so it loads even when the device/network
  // blocks the CDN directly (e.g. iPad WiFi blocking *.cloudfront.net or iCloud Private Relay).
  const clinicLogoSrc = clinicInfo?.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/clinic-logo/${encodeURIComponent(clinicCode)}`
    : undefined;
  const [lang, setLang] = useState(null); // null = language not yet selected
  const [step, setStep] = useState(0);
  const [submissionId] = useState(() => (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        })
  ));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [insuranceFront, setInsuranceFront] = useState(null);
  const [insuranceBack, setInsuranceBack] = useState(null);
  const [idPhoto, setIdPhoto] = useState(null);
  const sigRef = React.useRef(null);
  const [signatureData, setSignatureData] = useState(null);
  const [banner, setBanner] = useState(null); // { type: 'error'|'success', msg }
  const [form, setForm] = useState({
    first_name: '', last_name: '',
    address_street: '', address_city: '', address_state: '', address_zip: '',
    phone: '', email: '', preferred_contact: '',
    date_of_birth: '', sex: '', is_minor: 'No',
    allergies: 'Ninguno', other_allergy: '', other_allergy_detail: '', is_pregnant: '',
    has_emergency_contact: 'No',
    emergency_contact_first: '', emergency_contact_last: '',
    emergency_contact_relationship: '', emergency_contact_phone: '',
    taking_medications: 'No', medications_detail: '', referral_sources: [],
    pharmacy_name: '', pharmacy_street: '', pharmacy_city: '', pharmacy_state: '', pharmacy_zip: '', pharmacy_phone: '', pharmacy_pf_verified: null, pharmacy_pf_name: '',
    has_insurance: 'No',
    financial_name: '', financial_date: '',
    hipaa_name: '',
    telemedicine_name: '', telemedicine_dob: '', telemedicine_date: '',
    signature_name: '', representative_name: '', representative_relationship: '',
    sms_consent: false
  });

  const u = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const t = T[lang || 'es'];
  const clinicName = clinicInfo?.name || t.subtitle;
  const fill = (text) => (text || '').replace(/\{clinic\}/g, clinicName);

  // Practice Fusion pharmacy verification
  const [pharmacyVerify, setPharmacyVerify] = useState({ status: 'idle', pharmacies: [] });
  const verifyPharmacy = async ({ street, city, state, zip }) => {
    if (!clinicInfo?.id || !street || !city || !state) { setPharmacyVerify({ status: 'idle', pharmacies: [] }); return; }
    setPharmacyVerify({ status: 'checking', pharmacies: [] });
    try {
      const res = await api.get('/public/verify-pharmacy', {
        params: { clinic_id: clinicInfo.id, address: street, city, state, postal_code: zip || '' }
      });
      const d = res.data || {};
      const errorReasons = ['pf_error', 'exception', 'not_configured', 'incomplete_address'];
      if (!d.available || errorReasons.includes(d.reason)) {
        // PF unavailable / transient error — treat as unknown, do not warn staff as "not found"
        setPharmacyVerify({ status: 'idle', pharmacies: [] });
        u('pharmacy_pf_verified', null);
        u('pharmacy_pf_name', '');
        return;
      }
      if (d.matched) {
        setPharmacyVerify({ status: 'matched', pharmacies: d.pharmacies || [] });
        u('pharmacy_pf_verified', true);
        u('pharmacy_pf_name', d.pharmacies?.[0]?.name || '');
      } else {
        setPharmacyVerify({ status: 'nomatch', pharmacies: [] });
        u('pharmacy_pf_verified', false);
        u('pharmacy_pf_name', '');
      }
    } catch (e) {
      setPharmacyVerify({ status: 'error', pharmacies: [] });
    }
  };

  // Verify pharmacy whenever the address fields change (covers manual entry, not just Google selection)
  const pharmacyVerifyTimer = useRef(null);
  useEffect(() => {
    if (pharmacyVerifyTimer.current) clearTimeout(pharmacyVerifyTimer.current);
    const street = form.pharmacy_street, city = form.pharmacy_city, state = form.pharmacy_state, zip = form.pharmacy_zip;
    if (!street || !city || !state) {
      setPharmacyVerify({ status: 'idle', pharmacies: [] });
      return;
    }
    pharmacyVerifyTimer.current = setTimeout(() => {
      verifyPharmacy({ street, city, state, zip });
    }, 700);
    return () => { if (pharmacyVerifyTimer.current) clearTimeout(pharmacyVerifyTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pharmacy_street, form.pharmacy_city, form.pharmacy_state, form.pharmacy_zip]);

  useEffect(() => {
    if (!clinicCode) { setClinicInfo(null); return; }
    let active = true;
    api.get(`/public/clinic-by-code/${encodeURIComponent(clinicCode)}`)
      .then(res => { if (active) setClinicInfo(res.data); })
      .catch(() => { if (active) setClinicInfo(null); });
    return () => { active = false; };
  }, [clinicCode]);

  const steps = [
    { title: t.steps[0], icon: User },
    { title: t.steps[1], icon: Heart },
    { title: t.steps[2], icon: Building2 },
    { title: t.steps[3], icon: Shield },
    { title: t.steps[4], icon: FileText },
    { title: t.steps[5], icon: FileText },
    { title: t.steps[6], icon: FileText },
    { title: t.steps[7], icon: CheckCircle },
  ];

  const getStepError = () => {
    if (step === 0) {
      if (!form.first_name || !form.last_name) return t.vName;
      if (!form.address_street || !form.address_city || !form.address_state || !form.address_zip) return t.vAddress;
      if (!form.phone) return t.vPhone;
      if (!PHONE_RE.test(form.phone)) return t.vPhoneInvalid;
      if (!form.date_of_birth) return t.vDob;
      if (!DATE_RE.test(form.date_of_birth)) return t.vDateInvalid;
      if (!form.sex) return t.vSex;
      if (!form.preferred_contact) return t.vContact;
    }
    if (step === 1 && (!form.other_allergy || (form.sex !== t.male && !form.is_pregnant) || !form.has_emergency_contact || !form.taking_medications)) return t.vToggles;
    if (step === 2 && !form.pharmacy_name) return t.vPharmacy;
    if (step === 4 && !form.financial_name) return t.vFinancial;
    if (step === 5 && !form.hipaa_name) return t.vHipaa;
    if (step === 6) {
      if (!form.telemedicine_name) return t.vTele;
      if (form.telemedicine_dob && !DATE_RE.test(form.telemedicine_dob)) return t.vDateInvalid;
    }
    if (step === 7 && !signatureData) return t.vSig;
    if (step === 7 && !form.signature_name) return t.vSigName;
    return null;
  };

  const getInvalidFields = () => {
    const s = new Set();
    if (step === 0) {
      if (!form.first_name) s.add('first_name');
      if (!form.last_name) s.add('last_name');
      if (!form.address_street) s.add('address_street');
      if (!form.address_city) s.add('address_city');
      if (!form.address_state) s.add('address_state');
      if (!form.address_zip) s.add('address_zip');
      if (!form.phone || !PHONE_RE.test(form.phone)) s.add('phone');
      if (!form.date_of_birth || !DATE_RE.test(form.date_of_birth)) s.add('date_of_birth');
      if (!form.sex) s.add('sex');
      if (!form.preferred_contact) s.add('preferred_contact');
    }
    if (step === 1) {
      if (!form.other_allergy) s.add('other_allergy');
      if (form.sex !== t.male && !form.is_pregnant) s.add('is_pregnant');
      if (!form.has_emergency_contact) s.add('has_emergency_contact');
      if (!form.taking_medications) s.add('taking_medications');
    }
    if (step === 2 && !form.pharmacy_name) s.add('pharmacy_name');
    if (step === 4 && !form.financial_name) s.add('financial_name');
    if (step === 5 && !form.hipaa_name) s.add('hipaa_name');
    if (step === 6) {
      if (!form.telemedicine_name) s.add('telemedicine_name');
      if (form.telemedicine_dob && !DATE_RE.test(form.telemedicine_dob)) s.add('telemedicine_dob');
    }
    if (step === 7) {
      if (!signatureData) s.add('signature');
      if (!form.signature_name) s.add('signature_name');
    }
    return s;
  };
  const invalidFields = (banner && banner.type === 'error') ? getInvalidFields() : new Set();
  const errCls = (field) => (invalidFields.has(field) ? 'border-red-500 ring-2 ring-red-200' : '');

  // Live banner: once an error is shown, flip to green the moment the step becomes valid (and back to red if broken again)
  useEffect(() => {
    const err = getStepError();
    setBanner(prev => {
      if (!prev) return prev;
      if (prev.type === 'error' && !err) return { type: 'success', msg: t.looksGood };
      if (prev.type === 'success' && err) return { type: 'error', msg: err };
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, signatureData, step]);

  const validateStep = () => {
    const err = getStepError();
    if (err) { setBanner({ type: 'error', msg: err }); window.scrollTo(0, 0); return false; }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setBanner(null);
    // Auto-populate name/DOB fields for later steps
    const fullName = `${form.first_name} ${form.last_name}`.trim();
    const today = new Date();
    const todayStr = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}-${today.getFullYear()}`;
    if (step === 0) {
      setForm(p => ({
        ...p,
        financial_name: p.financial_name || fullName,
        hipaa_name: p.hipaa_name || fullName,
        telemedicine_name: p.telemedicine_name || fullName,
        telemedicine_dob: p.telemedicine_dob || p.date_of_birth,
        financial_date: p.financial_date || todayStr,
        telemedicine_date: p.telemedicine_date || todayStr,
        signature_name: p.signature_name || fullName,
      }));
    }
    setStep(s => Math.min(s + 1, steps.length - 1));
    window.scrollTo(0, 0);
  };
  const prev = () => { setBanner(null); setStep(s => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const dataUrlToBlob = (dataUrl) => {
    const [meta, b64] = dataUrl.split(',');
    const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/png';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const uploadIntakeFile = async (fileOrBlob, filename, field) => {
    const fd = new FormData();
    fd.append('file', fileOrBlob, filename);
    fd.append('clinic_code', clinicCode);
    fd.append('submission_id', submissionId);
    fd.append('field', field);
    const res = await api.post('/patient-intake/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.key;
  };

  // Cache uploaded keys so a submit retry reuses them instead of creating orphan objects.
  const uploadedRef = useRef({});
  const uploadCached = async (slot, identity, fileOrBlob, filename, field) => {
    const cached = uploadedRef.current[slot];
    if (cached && cached.id === identity) return cached.key;
    const key = await uploadIntakeFile(fileOrBlob, filename, field);
    uploadedRef.current[slot] = { id: identity, key };
    return key;
  };

  const toISODate = (val) => {
    if (!val) return val;
    const s = String(val).trim();
    // Already ISO (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // MM-DD-YYYY or MM/DD/YYYY -> YYYY-MM-DD
    const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) {
      const [, mm, dd, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    return s;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setBanner(null);
    try {
      // Upload photos to object storage first so we only persist small file keys
      // (not multi-MB base64) and never lose data to a silent background failure.
      let insurance_front_image = '', insurance_back_image = '', id_photo_image = '', signature_image = '';
      if (insuranceFront) insurance_front_image = await uploadCached('insuranceFront', insuranceFront, insuranceFront, insuranceFront.name || 'insurance-front.jpg', 'insurance_front');
      if (insuranceBack) insurance_back_image = await uploadCached('insuranceBack', insuranceBack, insuranceBack, insuranceBack.name || 'insurance-back.jpg', 'insurance_back');
      if (idPhoto) id_photo_image = await uploadCached('idPhoto', idPhoto, idPhoto, idPhoto.name || 'id-photo.jpg', 'id_photo');
      if (signatureData) signature_image = await uploadCached('signature', signatureData, dataUrlToBlob(signatureData), 'signature.png', 'signature');

      const payload = {
        ...form,
        id: submissionId,
        clinic_code: clinicCode,
        referral_source: (form.referral_sources || []).join(', '),
        emergency_contact_name: `${form.emergency_contact_first} ${form.emergency_contact_last}`.trim(),
        date_of_birth: toISODate(form.date_of_birth),
        telemedicine_dob: toISODate(form.telemedicine_dob),
        insurance_front_image, insurance_back_image, id_photo_image,
        signature_image
      };

      // Wait for the server to confirm receipt before showing success so a network
      // failure surfaces an error the patient can retry instead of silently losing data.
      await api.post('/patient-intake', payload);
      setSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      console.error('Patient intake submission failed:', error);
      setBanner({ type: 'error', msg: t.vError });
      window.scrollTo(0, 0);
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-teal-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">{t.successTitle}</h1>
          <p className="text-lg text-slate-600 mb-8">{t.successMsg}</p>
          <Button onClick={() => window.location.reload()} variant="outline" size="lg" className="text-lg h-14 px-8">{t.newForm}</Button>
        </div>
      </div>
    );
  }

  // Clinic code validation gate
  if (clinicInfo === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6" data-testid="intake-loading">
        <p className="text-lg text-slate-500">Loading…</p>
      </div>
    );
  }
  if (clinicInfo === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6" data-testid="intake-invalid-clinic">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid or missing clinic link</h1>
          <p className="text-base text-slate-500">Please use the correct intake link provided by your clinic. The link should include a valid clinic code (e.g. <span className="font-mono">?clinic=TCF</span>).</p>
        </div>
      </div>
    );
  }

  // Language selection screen
  if (lang === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <ClinicLogo
            logoUrl={clinicLogoSrc}
            clinicName={clinicInfo?.name}
            className="h-16 w-auto max-w-[220px] object-contain mx-auto mb-6"
            textClassName="block text-2xl font-bold text-slate-900 mb-6"
          />
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Seleccione su idioma / Select your language</h1>
          <p className="text-base text-slate-500 mb-8">{clinicInfo?.name || 'Tu Clinica Familiar'}</p>
          <div className="flex flex-col gap-4">
            <button onClick={() => setLang('es')} className="w-full py-5 rounded-xl border-2 border-slate-200 text-xl font-semibold text-slate-800 hover:border-blue-500 hover:bg-blue-50 transition-all">
              Español
            </button>
            <button onClick={() => setLang('en')} className="w-full py-5 rounded-xl border-2 border-slate-200 text-xl font-semibold text-slate-800 hover:border-blue-500 hover:bg-blue-50 transition-all">
              English
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Header with Logo */}
      <div className="bg-white border-b border-slate-200 py-3 px-6 shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          <ClinicLogo
            logoUrl={clinicLogoSrc}
            clinicName={clinicInfo?.name}
            className="h-10 w-auto max-w-[160px] object-contain"
            textClassName="text-lg font-bold text-slate-900"
          />
          <div className="text-center">
            <h1 className="text-lg font-bold text-slate-900">{t.formTitle}</h1>
          </div>
        </div>
      </div>

      {/* Full-width status banner */}
      {banner && (
        <div className={`w-full px-6 py-4 flex items-center justify-center gap-3 shrink-0 text-white ${banner.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`} data-testid="intake-error-banner">
          {banner.type === 'success' ? <CheckCircle className="w-6 h-6 shrink-0" /> : <X className="w-6 h-6 shrink-0" />}
          <span className="text-base sm:text-lg font-semibold text-center">{banner.msg}</span>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <StepIndicator steps={steps} current={step} />

          <div className="flex items-center gap-2 mb-4">
            {React.createElement(steps[step].icon, { className: 'w-5 h-5 text-blue-600' })}
            <h2 className="text-xl font-bold text-slate-900">{steps[step].title}</h2>
          </div>

        {/* Step 0: Personal Info */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-base font-semibold text-slate-700">{t.firstName} <span className="text-red-500">*</span></Label><Input value={form.first_name} onChange={e => u('first_name', e.target.value)} placeholder={t.firstName} className={`h-14 text-lg mt-1 ${errCls('first_name')}`} /></div>
              <div><Label className="text-base font-semibold text-slate-700">{t.lastName} <span className="text-red-500">*</span></Label><Input value={form.last_name} onChange={e => u('last_name', e.target.value)} placeholder={t.lastName} className={`h-14 text-lg mt-1 ${errCls('last_name')}`} /></div>
            </div>
            <div><Label className="text-base font-semibold text-slate-700">{t.address} <span className="text-red-500">*</span></Label><AddressAutocompleteInput apiKey={clinicInfo?.google_places_api_key} value={form.address_street} onChange={v => { u('address_street', v); if (!v) { u('address_city', ''); u('address_state', ''); u('address_zip', ''); } }} onSelect={({ street, city, state, zip }) => { u('address_street', street); if (city) u('address_city', city); if (state) u('address_state', state); if (zip) u('address_zip', zip); }} placeholder={t.street} className={`h-14 text-lg mt-1 ${errCls('address_street')}`} testId="address-street-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <Input value={form.address_city} onChange={e => u('address_city', e.target.value)} placeholder={t.city} className={`h-14 text-lg ${errCls('address_city')}`} />
              <Select value={form.address_state} onValueChange={v => u('address_state', v)}><SelectTrigger className={`h-14 text-lg ${errCls('address_state')}`}><SelectValue placeholder={t.state} /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Input value={form.address_zip} onChange={e => u('address_zip', e.target.value)} placeholder={t.zip} className={`h-14 text-lg ${errCls('address_zip')}`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-base font-semibold text-slate-700">{t.phone} <span className="text-red-500">*</span></Label><PhoneInput value={form.phone} onChange={v => u('phone', v)} testid="phone-input" invalid={invalidFields.has('phone')} /></div>
              <div><Label className="text-base font-semibold text-slate-700">{t.email}</Label><Input type="email" value={form.email} onChange={e => u('email', e.target.value)} placeholder="email@ejemplo.com" className="h-14 text-lg mt-1" /></div>
            </div>
            <div><Label className="text-base font-semibold text-slate-700">{t.contactMethod} <span className="text-red-500">*</span></Label><div className="mt-1"><ToggleGroup options={[t.email || 'Email', t.call, 'SMS']} value={form.preferred_contact} onChange={v => u('preferred_contact', v)} invalid={invalidFields.has('preferred_contact')} /></div></div>

            <hr className="border-slate-200" />

            <div>
              <Label className="text-base font-semibold text-slate-700">{t.dob} <span className="text-red-500">*</span></Label>
              <DateInput value={form.date_of_birth} onChange={v => u('date_of_birth', v)} testid="dob-input" invalid={invalidFields.has('date_of_birth')} />
            </div>
            <div>
              <Label className="text-base font-semibold text-slate-700">{t.sex} <span className="text-red-500">*</span></Label>
              <div className="mt-1"><ToggleGroup options={[t.male, t.female]} value={form.sex} onChange={v => u('sex', v)} invalid={invalidFields.has('sex')} /></div>
            </div>
            <div><Label className="text-base font-semibold text-slate-700">{t.isMinor}</Label><div className="mt-1"><ToggleGroup options={[t.yes, t.no]} value={form.is_minor} onChange={v => u('is_minor', v)} /></div></div>
          </div>
        )}

        {/* Step 1: Medical */}
        {step === 1 && (
          <div className="space-y-5">
            <div><Label className="text-base font-semibold text-slate-700">{t.allergies} <span className="text-red-500">*</span></Label>
              <Select value={form.allergies} onValueChange={v => u('allergies', v)}><SelectTrigger className="h-14 text-lg mt-1"><SelectValue /></SelectTrigger><SelectContent>{ALLERGY_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-base font-semibold text-slate-700">{t.otherAllergy} <span className="text-red-500">*</span></Label><div className="mt-1"><ToggleGroup options={[t.no, t.yes]} value={form.other_allergy} onChange={v => u('other_allergy', v)} invalid={invalidFields.has('other_allergy')} /></div>
              {(form.other_allergy === t.yes) && <Input value={form.other_allergy_detail} onChange={e => u('other_allergy_detail', e.target.value)} placeholder={t.specify} className="h-14 text-lg mt-2" />}
            </div>
            {form.sex !== t.male && (
              <div><Label className="text-base font-semibold text-slate-700">{t.pregnant} <span className="text-red-500">*</span></Label><div className="mt-1"><ToggleGroup options={[t.yes, t.no]} value={form.is_pregnant} onChange={v => u('is_pregnant', v)} invalid={invalidFields.has('is_pregnant')} /></div></div>
            )}

            <hr className="border-slate-200" />

            <div>
              <Label className="text-base font-semibold text-slate-700">{t.emergencyContact}</Label><div className="mt-1"><ToggleGroup options={[t.yes, t.no]} value={form.has_emergency_contact} onChange={v => u('has_emergency_contact', v)} invalid={invalidFields.has('has_emergency_contact')} /></div>
              {(form.has_emergency_contact === t.yes) && (
                <div className="space-y-3 mt-3 p-4 bg-slate-50 rounded-xl">
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={form.emergency_contact_first} onChange={e => u('emergency_contact_first', e.target.value)} placeholder={t.firstName} className="h-14 text-lg" />
                    <Input value={form.emergency_contact_last} onChange={e => u('emergency_contact_last', e.target.value)} placeholder={t.lastName} className="h-14 text-lg" />
                  </div>
                  <Input value={form.emergency_contact_relationship} onChange={e => u('emergency_contact_relationship', e.target.value)} placeholder={t.relationship} className="h-14 text-lg" />
                  <Input type="tel" value={form.emergency_contact_phone} onChange={e => u('emergency_contact_phone', e.target.value)} placeholder={t.phone} className="h-14 text-lg" />
                </div>
              )}
            </div>

            <hr className="border-slate-200" />

            <div>
              <Label className="text-base font-semibold text-slate-700">{t.takingMeds} <span className="text-red-500">*</span></Label><div className="mt-1"><ToggleGroup options={[t.yes, t.no]} value={form.taking_medications} onChange={v => u('taking_medications', v)} invalid={invalidFields.has('taking_medications')} /></div>
              {(form.taking_medications === t.yes) && <textarea value={form.medications_detail} onChange={e => u('medications_detail', e.target.value)} placeholder={t.medsDetail} rows={2} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-lg resize-none focus:ring-2 focus:ring-blue-400 outline-none" />}
            </div>

            <hr className="border-slate-200" />

            <div><Label className="text-base font-semibold text-slate-700">{t.referral}</Label>
              <MultiSelect options={t.referralOptions} values={form.referral_sources} onChange={v => u('referral_sources', v)} placeholder={t.selectMultiple} />
            </div>
          </div>
        )}

        {/* Step 2: Pharmacy */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-base text-blue-800 leading-relaxed">{fill(t.pharmacyIntro)}</div>
            <hr className="border-slate-200" />
            <div><Label className="text-base font-semibold text-slate-700">{t.pharmacyName} <span className="text-red-500">*</span></Label><PharmacyInput value={form.pharmacy_name} onChange={v => u('pharmacy_name', v)} placeholder={t.pharmacyName} invalid={invalidFields.has('pharmacy_name')} /></div>
            <div><Label className="text-base font-semibold text-slate-700">{t.pharmacyAddress}</Label><AddressAutocompleteInput apiKey={clinicInfo?.google_places_api_key} queryPrefix={form.pharmacy_name} value={form.pharmacy_street} onChange={v => { u('pharmacy_street', v); if (!v) { setPharmacyVerify({ status: 'idle', pharmacies: [] }); u('pharmacy_pf_verified', null); u('pharmacy_pf_name', ''); } }} onSelect={({ street, city, state, zip, phone }) => { u('pharmacy_street', street); if (city) u('pharmacy_city', city); if (state) u('pharmacy_state', state); if (zip) u('pharmacy_zip', zip); if (phone) u('pharmacy_phone', phone); }} placeholder={t.street} className="h-14 text-lg mt-1" testId="pharmacy-street-input" />
              {pharmacyVerify.status === 'checking' && (
                <p className="mt-2 flex items-center gap-2 text-sm text-slate-500" data-testid="pharmacy-verify-status"><RotateCcw className="h-4 w-4 animate-spin" /> {t.pfChecking}</p>
              )}
              {pharmacyVerify.status === 'matched' && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="pharmacy-verify-status">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <span>{t.pfMatched}{pharmacyVerify.pharmacies[0]?.name ? ` — ${pharmacyVerify.pharmacies[0].name}` : ''}</span>
                </div>
              )}
              {pharmacyVerify.status === 'nomatch' && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="pharmacy-verify-status">
                  <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <span>{t.pfNoMatch}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input value={form.pharmacy_city} onChange={e => u('pharmacy_city', e.target.value)} placeholder={t.city} className="h-14 text-lg" />
              <Select value={form.pharmacy_state} onValueChange={v => u('pharmacy_state', v)}><SelectTrigger className="h-14 text-lg"><SelectValue placeholder={t.state} /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Input value={form.pharmacy_zip} onChange={e => u('pharmacy_zip', e.target.value)} placeholder={t.zip} className="h-14 text-lg" />
            </div>
            <div><Label className="text-base font-semibold text-slate-700">{t.phone}</Label><PhoneInput value={form.pharmacy_phone} onChange={v => u('pharmacy_phone', v)} testid="pharmacy-phone-input" /></div>
          </div>
        )}

        {/* Step 3: Insurance & Documents */}
        {step === 3 && (
          <div className="space-y-5">
            <div><Label className="text-base font-semibold text-slate-700">{t.hasInsurance} <span className="text-red-500">*</span></Label><div className="mt-1"><ToggleGroup options={[t.yes, t.no]} value={form.has_insurance} onChange={v => u('has_insurance', v)} /></div></div>
            {(form.has_insurance === t.yes) && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <PhotoUpload label={t.insuranceFront} file={insuranceFront} onUpload={setInsuranceFront} onRemove={() => setInsuranceFront(null)} promptText={t.photoPrompt} testid="insurance-front-input" />
                <PhotoUpload label={t.insuranceBack} file={insuranceBack} onUpload={setInsuranceBack} onRemove={() => setInsuranceBack(null)} promptText={t.photoPrompt} testid="insurance-back-input" />
              </div>
            )}

            <hr className="border-slate-200" />

            <PhotoUpload label={t.idPhoto} file={idPhoto} onUpload={setIdPhoto} onRemove={() => setIdPhoto(null)} promptText={t.photoPrompt} testid="id-photo-input" />
            {(form.has_insurance === t.yes) && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-base text-amber-800 font-medium">{t.insuranceNote}</div>
            )}
          </div>
        )}

        {/* Step 4: Financial Responsibility */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 text-base text-slate-700 max-h-72 overflow-y-auto leading-relaxed whitespace-pre-line">
              <p className="font-bold text-slate-900 text-lg mb-3">{t.financialTitle}</p>
              <p className="mb-3">{fill(t.financialText)}</p>
              <p className="font-semibold text-slate-900">{fill(t.financialAgree)}</p>
            </div>
            <hr className="border-slate-200" />
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-base font-semibold text-slate-700">{t.fullName} <span className="text-red-500">*</span></Label><Input value={form.financial_name} onChange={e => u('financial_name', e.target.value)} placeholder={t.fullName} className={`h-14 text-lg mt-1 ${errCls('financial_name')}`} data-testid="financial-name-input" /></div>
              <div><Label className="text-base font-semibold text-slate-700">{t.dateLabel}</Label><DateInput value={form.financial_date} onChange={v => u('financial_date', v)} testid="financial-date-input" /></div>
            </div>
          </div>
        )}

        {/* Step 5: HIPAA */}
        {step === 5 && (
          <div className="space-y-5">
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 text-base text-slate-700 max-h-72 overflow-y-auto leading-relaxed whitespace-pre-line">
              <p className="font-bold text-slate-900 text-lg mb-3">{t.hipaaTitle}</p>
              <p>{fill(t.hipaaFull)}</p>
            </div>
            <hr className="border-slate-200" />
            <div><Label className="text-base font-semibold text-slate-700">{t.hipaaSignLabel} <span className="text-red-500">*</span></Label><Input value={form.hipaa_name} onChange={e => u('hipaa_name', e.target.value)} placeholder={t.fullName} className={`h-14 text-lg mt-1 ${errCls('hipaa_name')}`} data-testid="hipaa-name-input" /></div>
          </div>
        )}

        {/* Step 6: Telemedicine Informed Consent */}
        {step === 6 && (
          <div className="space-y-5">
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 text-base text-slate-700 max-h-72 overflow-y-auto leading-relaxed whitespace-pre-line">
              <p className="font-bold text-slate-900 text-lg mb-3">{t.teleTitle}</p>
              <p>{fill(t.teleFull)}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800 leading-relaxed">{t.teleFooter}</div>
            <hr className="border-slate-200" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-base font-semibold text-slate-700">{t.fullName} <span className="text-red-500">*</span></Label><Input value={form.telemedicine_name} onChange={e => u('telemedicine_name', e.target.value)} placeholder={t.fullName} className={`h-14 text-lg mt-1 ${errCls('telemedicine_name')}`} data-testid="tele-name-input" /></div>
              <div><Label className="text-base font-semibold text-slate-700">{t.dobLabel}</Label><DateInput value={form.telemedicine_dob} onChange={v => u('telemedicine_dob', v)} testid="tele-dob-input" invalid={invalidFields.has('telemedicine_dob')} /></div>
              <div><Label className="text-base font-semibold text-slate-700">{t.dateLabel}</Label><DateInput value={form.telemedicine_date} onChange={v => u('telemedicine_date', v)} testid="tele-date-input" /></div>
            </div>
          </div>
        )}

        {/* Step 7: Signature & Agreement */}
        {step === 7 && (
          <div className="space-y-5">
            {/* Signature Pad */}
            <div>
              <Label className="text-base font-semibold text-slate-700">{t.signatureLabel} <span className="text-red-500">*</span></Label>
              <div className={`mt-2 border-2 rounded-xl overflow-hidden bg-white relative ${invalidFields.has('signature') ? 'border-red-500 ring-2 ring-red-200' : 'border-slate-300'}`} style={{ touchAction: 'none' }}>
                <React.Suspense fallback={<div className="w-full" style={{ height: '160px' }} />}>
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#1e293b"
                    canvasProps={{ className: 'w-full', style: { width: '100%', height: '160px' } }}
                    onEnd={() => {
                      if (sigRef.current && !sigRef.current.isEmpty()) {
                        setSignatureData(sigRef.current.toDataURL('image/png'));
                      }
                    }}
                  />
                </React.Suspense>
                <button
                  type="button"
                  onClick={() => { sigRef.current?.clear(); setSignatureData(null); }}
                  className="absolute top-2 right-2 flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-600 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> {t.clear}
                </button>
                {!signatureData && (
                  <p className="absolute bottom-2 left-0 right-0 text-center text-sm text-slate-400 pointer-events-none">{t.signHere}</p>
                )}
              </div>
            </div>

            <div><Label className="text-base font-semibold text-slate-700">{t.fullName} <span className="text-red-500">*</span></Label><Input value={form.signature_name} onChange={e => u('signature_name', e.target.value)} placeholder={t.fullName} className={`h-14 text-lg mt-1 border-2 ${invalidFields.has('signature_name') ? 'border-red-500 ring-2 ring-red-200' : 'border-slate-400'}`} data-testid="signature-name-input" /></div>

            <hr className="border-slate-200" />

            <div>
              <Label className="text-base font-semibold text-slate-700">{t.representative}</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Input value={form.representative_name} onChange={e => u('representative_name', e.target.value)} placeholder={t.fullName} className="h-14 text-lg" />
                <Input value={form.representative_relationship} onChange={e => u('representative_relationship', e.target.value)} placeholder={t.repRelationship} className="h-14 text-lg" />
              </div>
            </div>

            <hr className="border-slate-200" />

            <div>
              <p className="text-base font-bold text-slate-900 mb-2">{t.agreementTitle}</p>
              <label className="flex items-start gap-3 cursor-pointer p-4 bg-slate-50 rounded-xl border border-slate-200">
                <input type="checkbox" checked={form.sms_consent} onChange={e => u('sms_consent', e.target.checked)} className="w-6 h-6 mt-0.5 text-violet-600 rounded" data-testid="sms-consent-checkbox" />
                <span className="text-sm text-slate-600 leading-relaxed">{t.smsConsent}</span>
              </label>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* Fixed bottom navigation */}
      <div className="shrink-0 bg-white border-t border-slate-200 px-6 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {step > 0 ? (
            <Button variant="outline" size="lg" onClick={prev} className="h-14 px-6 text-lg">
              <ChevronLeft className="w-5 h-5 mr-1" /> {t.back}
            </Button>
          ) : <div />}
          
          {step < steps.length - 1 ? (
            <Button size="lg" onClick={next} className="h-14 px-8 text-lg bg-blue-600 hover:bg-blue-700 text-white">
              {t.next} <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button size="lg" onClick={handleSubmit} disabled={submitting} className="h-14 px-8 text-lg bg-teal-600 hover:bg-teal-700 text-white" data-testid="intake-submit-button">
              {submitting ? (<><RotateCcw className="w-5 h-5 mr-2 animate-spin" /> {t.submitting}</>) : t.submit}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientIntake;
